import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Ear, ArrowLeft, Upload, File, X, CheckCircle, ArrowClockwise,
  MagnifyingGlass, Envelope, Printer, FloppyDisk
} from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useOrderForm } from './useOrderForm';
import OrderFormFields from './OrderFormFields';

export default function OrderForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft');
  const editId = searchParams.get('edit');
  const { user } = useAuth();
  const fileInputLeft = useRef(null);
  const fileInputRight = useRef(null);

  const [products, setProducts] = useState([]);
  const form = useOrderForm(products);

  const [stlLeft, setStlLeft] = useState(null);
  const [stlRight, setStlRight] = useState(null);
  const [envoiCourrier, setEnvoiCourrier] = useState(false);
  // Reuse-impressions feature: re-use STL(s) from previous orders sharing the same patient_reference.
  const [reuseImpressions, setReuseImpressions] = useState(false);
  const [reuseAvailable, setReuseAvailable] = useState(null); // null = unchecked / {available, sides}

  // Fetch client smart defaults
  useEffect(() => {
    if (draftId || editId) return; // Don't override loaded values
    const fetchDefaults = async () => {
      try {
        const res = await axios.get(`${API}/orders/client-defaults`);
        const data = res.data;
        if (data.last_validation_email) setLastValidationEmail(data.last_validation_email);
        if (data.personalized && data.defaults) {
          const d = data.defaults;
          // IMPORTANT: order matters — some setters reset dependent fields.
          // Set high-level dimensions first (cascade setters clear children),
          // then children values so they stick.
          if (d.fabrication_type) form.setFabricationType(d.fabrication_type);
          if (d.embout_type) form.setEmboutType(d.embout_type);
          if (d.marque) form.setMarque(d.marque);
          // Now set children that cascade-setters would have cleared:
          if (d.forme_embout) form.setFormeEmbout(d.forme_embout);
          if (d.ear_side) form.setEarSide(d.ear_side);
          if (d.matiere) form.setMatiere(d.matiere);
          if (d.durete) form.setDurete(d.durete);
          if (d.couleur) form.setCouleur(d.couleur);
          if (d.etancheite) form.setEtancheite(d.etancheite);
          if (d.finition) form.setFinition(d.finition);
          if (d.texture_oreille) form.setTextureOreille(d.texture_oreille);
          if (d.patient_type) form.setPatientType(d.patient_type);
          // Set montage_type LAST so it overrides the one auto-set by setMarque
          if (d.montage_type) form.setMontageType(d.montage_type);
          // Events: only when a side default exists and the side is ordered
          if (d.event_od && (d.ear_side === 'right' || d.ear_side === 'both')) form.setEventOD(d.event_od);
          if (d.event_og && (d.ear_side === 'left' || d.ear_side === 'both')) form.setEventOG(d.event_og);
        } else {
          // Global defaults for new clients
          form.setFormeEmbout('canule');
          form.setEventOD('2');
          form.setEventOG('2');
        }
      } catch {
        // Fallback global defaults
        form.setFormeEmbout('canule');
        form.setEventOD('2');
        form.setEventOG('2');
      }
    };
    fetchDefaults();
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isRefab, setIsRefab] = useState(false);
  const [refabSerial, setRefabSerial] = useState('');
  const [refabInfo, setRefabInfo] = useState(null);
  const [refabDialogOpen, setRefabDialogOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(draftId || null);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [wants3dValidation, setWants3dValidation] = useState(false);
  const [validationEmail, setValidationEmail] = useState('');
  const [lastValidationEmail, setLastValidationEmail] = useState('');

  // Establishment selection for multi-establishment clients
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishment, setSelectedEstablishment] = useState('');

  // Check if previous impressions exist for this patient_reference (debounced)
  useEffect(() => {
    if (editId) return;
    const pr = (form.patientRef || '').trim();
    if (!pr || pr.length < 2 || isRefab) {
      setReuseAvailable(null);
      setReuseImpressions(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/orders/client/has-impressions`, { params: { patient_reference: pr } });
        setReuseAvailable(r.data);
        if (!r.data?.available) setReuseImpressions(false);
      } catch {
        setReuseAvailable(null);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.patientRef, isRefab, editId]);



  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
        // Load draft after products are loaded
        if (draftId && !draftLoaded) {
          try {
            const draftRes = await axios.get(`${API}/orders/${draftId}`);
            if (draftRes.data && draftRes.data.status === 'draft') {
              form.loadFromOrder(draftRes.data);
              if (draftRes.data.options?.envoi_courrier) setEnvoiCourrier(true);
              if (draftRes.data.options?.wants_3d_validation) {
                setWants3dValidation(true);
                setValidationEmail(draftRes.data.validation_email || draftRes.data.options?.validation_email || '');
              }
              if (draftRes.data.establishment_id) setSelectedEstablishment(draftRes.data.establishment_id);
              setDraftLoaded(true);
              toast.info('Brouillon chargé — complétez et validez votre commande');
            }
          } catch { toast.error('Impossible de charger le brouillon'); }
        }
        // Load existing order in edit mode
        if (editId && !draftLoaded) {
          try {
            const orderRes = await axios.get(`${API}/orders/${editId}`);
            if (orderRes.data && orderRes.data.status === 'pending') {
              form.loadFromOrder(orderRes.data);
              if (orderRes.data.options?.envoi_courrier) setEnvoiCourrier(true);
              if (orderRes.data.wants_3d_validation || orderRes.data.options?.wants_3d_validation) {
                setWants3dValidation(true);
                setValidationEmail(orderRes.data.validation_email || orderRes.data.options?.validation_email || '');
              }
              if (orderRes.data.establishment_id) setSelectedEstablishment(orderRes.data.establishment_id);
              setDraftLoaded(true);
              toast.info('Modification de la commande — enregistrez vos changements');
            } else {
              toast.error("Cette commande ne peut plus être modifiée");
            }
          } catch { toast.error('Impossible de charger la commande'); }
        }
      } catch (e) { console.error('Error loading products', e); }
    };
    fetchProducts();
    // Fetch establishments for this client (endpoint autorisé pour l'utilisateur lui-même)
    const estIds = user?.establishment_ids || [];
    if (estIds.length >= 1) {
      const fetchEstablishments = async () => {
        try {
          const res = await axios.get(`${API}/users/${user.id}/establishments`);
          setEstablishments(res.data || []);
        } catch (e) { console.error('Error loading establishments', e); }
      };
      fetchEstablishments();
    }
    if (estIds.length === 1) {
      setSelectedEstablishment(estIds[0]);
    }
  }, [user]);

  const handleFileSelect = (side, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.stl')) { toast.error('Veuillez sélectionner un fichier STL'); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error('Le fichier ne doit pas dépasser 15 Mo'); return; }
    if (side === 'left') setStlLeft(file); else setStlRight(file);
    toast.success(`Fichier ${side === 'left' ? 'OG' : 'OD'} sélectionné`);
  };
  const handleDrop = (e, side) => { e.preventDefault(); e.stopPropagation(); handleFileSelect(side, e.dataTransfer.files[0]); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  const checkRefabrication = async () => {
    if (!refabSerial.trim()) { toast.error('Veuillez entrer un numéro de série'); return; }
    try {
      const response = await axios.post(`${API}/orders/check-refabrication?serial_number=${refabSerial}`);
      setRefabInfo(response.data);
      setRefabDialogOpen(true);
    } catch (error) { toast.error(error.response?.data?.detail || 'Numéro de série non trouvé'); }
  };
  const confirmRefabrication = () => { form.setPatientRef(refabInfo.patient_reference); setRefabDialogOpen(false); toast.success('Informations de refabrication chargées'); };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      const formData = form.buildFormData();
      formData.options.envoi_courrier = envoiCourrier;
      formData.options.wants_3d_validation = wants3dValidation;
      formData.options.validation_email = wants3dValidation ? (validationEmail || '').trim().toLowerCase() : null;
      formData.is_refabrication = isRefab;
      formData.original_serial_number = isRefab ? refabSerial : null;
      if (selectedEstablishment) formData.establishment_id = selectedEstablishment;

      let res;
      if (currentDraftId) {
        res = await axios.put(`${API}/orders/draft/${currentDraftId}`, formData);
      } else {
        res = await axios.post(`${API}/orders/draft`, formData);
        setCurrentDraftId(res.data.id);
        // Update URL without reload
        window.history.replaceState(null, '', `/client/order?draft=${res.data.id}`);
      }
      toast.success('Brouillon sauvegardé');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Erreur lors de la sauvegarde du brouillon'));
    } finally { setIsSavingDraft(false); }
  };

  const uploadSTLFile = async (orderId, file, side) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/orders/${orderId}/upload-stl?side=${side}`, formData);
      return true;
    } catch (error) { console.error('Upload failed:', error); return false; }
  };

  const generateBordereauPDF = (order) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Popup bloqué. Autorisez les popups pour imprimer le bordereau.'); return; }
    const htmlContent = `<!DOCTYPE html><html><head><title>Bordereau d'envoi</title>
      <style>body{font-family:Arial,sans-serif;padding:40px;max-width:800px;margin:0 auto}
      .header{text-align:center;border-bottom:3px solid #333;padding-bottom:20px;margin-bottom:30px}
      .addresses{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin:20px 0}
      .address-box{border:2px solid #333;padding:20px;font-size:16px;line-height:1.8}
      .address-box .label{font-size:11px;text-transform:uppercase;letter-spacing:2px;color:#666;font-weight:700;margin-bottom:8px}
      .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:15px;margin:20px 0}
      .info-item{padding:10px;border:1px solid #ddd;border-radius:4px}
      .info-label{font-weight:bold;color:#666;font-size:12px;text-transform:uppercase;letter-spacing:1px}
      .footer{margin-top:40px;text-align:center;color:#666;font-size:12px;border-top:1px solid #ddd;padding-top:20px}
      @media print{body{padding:20px}}</style></head><body></body></html>`;
    const doc = printWindow.document;
    doc.open();
    doc.write(htmlContent);
    doc.close();
    const body = doc.body;
    const header = doc.createElement('div');
    header.className = 'header';
    const h1 = doc.createElement('h1');
    h1.textContent = 'HAMAC SOLUTIONS';
    const h2 = doc.createElement('h2');
    h2.textContent = "Bordereau d'envoi - Moulages physiques";
    header.appendChild(h1);
    header.appendChild(h2);
    body.appendChild(header);
    const addresses = doc.createElement('div');
    addresses.className = 'addresses';
    const sender = doc.createElement('div');
    sender.className = 'address-box';
    sender.textContent = '';
    const senderLabel = doc.createElement('div');
    senderLabel.className = 'label';
    senderLabel.textContent = 'Expéditeur';
    sender.appendChild(senderLabel);
    const senderName = doc.createElement('strong');
    senderName.textContent = user?.name || '';
    sender.appendChild(senderName);
    sender.appendChild(doc.createElement('br'));
    sender.appendChild(doc.createTextNode(user?.company_name || ''));
    sender.appendChild(doc.createElement('br'));
    sender.appendChild(doc.createTextNode(user?.address || 'Adresse non renseignée'));
    addresses.appendChild(sender);
    const recipient = doc.createElement('div');
    recipient.className = 'address-box';
    const recipientLabel = doc.createElement('div');
    recipientLabel.className = 'label';
    recipientLabel.textContent = 'Destinataire';
    recipient.appendChild(recipientLabel);
    const recipientName = doc.createElement('strong');
    recipientName.textContent = 'HAMAC SOLUTIONS';
    recipient.appendChild(recipientName);
    recipient.appendChild(doc.createElement('br'));
    recipient.appendChild(doc.createTextNode('67 rue de la République'));
    recipient.appendChild(doc.createElement('br'));
    recipient.appendChild(doc.createTextNode('69220 Belleville'));
    addresses.appendChild(recipient);
    body.appendChild(addresses);
    const infoGrid = doc.createElement('div');
    infoGrid.className = 'info-grid';
    const addInfoItem = (label, value) => {
      const item = doc.createElement('div');
      item.className = 'info-item';
      const l = doc.createElement('div');
      l.className = 'info-label';
      l.textContent = label;
      item.appendChild(l);
      item.appendChild(doc.createTextNode(value));
      infoGrid.appendChild(item);
    };
    addInfoItem('Référence patient', order.patient_reference);
    addInfoItem('N° de commande', order.id?.substring(0, 8));
    addInfoItem('Côté', order.ear_side === 'left' ? 'OG' : order.ear_side === 'right' ? 'OD' : 'Bilatéral');
    addInfoItem('Produit', order.product_name);
    body.appendChild(infoGrid);
    const footer = doc.createElement('div');
    footer.className = 'footer';
    footer.textContent = 'Document généré automatiquement - HAMAC SOLUTIONS';
    body.appendChild(footer);
    printWindow.onload = () => printWindow.print();
  };

  const openOrderRecap = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/recap`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        toast.error('Popup bloqué. Autorisez les popups pour afficher le récapitulatif.');
      }
      // Revoke after a few seconds to keep memory clean; the window still holds the document
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Erreur lors de l'ouverture du récapitulatif");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patientRef.trim()) { toast.error('Veuillez entrer une référence patient'); return; }
    if (!form.earSide) { toast.error('Veuillez sélectionner un côté (OD/OG/Bilatéral)'); return; }
    if (establishments.length > 1 && !selectedEstablishment) { toast.error('Veuillez sélectionner un établissement'); return; }
    if (!form.resolvedProductKey || !form.resolvedProduct) {
      if (form.fabricationType === 'obturateur' && !form.obturateurType) toast.error("Veuillez sélectionner un type d'obturateur");
      else if (form.fabricationType === 'embout_auditif' && !form.formeEmbout) toast.error("Veuillez sélectionner une forme d'embout");
      else toast.error('Produit introuvable. Vérifiez vos choix.');
      return;
    }
    // Conformateur d'oreille : description obligatoire (min 10 caractères)
    if (form.fabricationType === 'conformateur_oreille') {
      const desc = (form.conformateurDescription || '').trim();
      if (desc.length < 10) {
        toast.error("Merci de décrire votre besoin pour le conformateur d'oreille (10 caractères minimum).");
        return;
      }
    }
    if (form.emboutType === 'integral') {
      if (!form.ecouteurVersion) { toast.error("Veuillez sélectionner la version de l'écouteur (5.0 ou 6.0)"); return; }
      if (!form.ecouteurPuissance) { toast.error("Veuillez sélectionner la puissance de l'écouteur"); return; }
      if (!form.integralCableLength) { toast.error("Veuillez sélectionner la longueur du câble"); return; }
      if (form.ecouteurVersion === '5.0' && form.integralCableLength === '00') {
        toast.error("La longueur de câble '00' n'est disponible que pour la version 6.0"); return;
      }
    }
    // Bague 3DSA (Phonak RIC) : consentement + option de repli obligatoires
    if (form.emboutType === 'ecouteur_deporte' && form.montageType === 'montage_bague_3dsa') {
      if (!form.bague3dsaConsent) {
        toast.error("Merci de cocher votre consentement pour un éventuel changement d'option si la bague 3DSA ne passe pas dans le conduit.");
        return;
      }
      if (!form.bague3dsaFallback) {
        toast.error("Merci de choisir votre option de repli (SlimTip Link ou Click tube) au cas où la bague 3DSA ne passerait pas.");
        return;
      }
    }
    if (!isRefab && !envoiCourrier && !reuseImpressions) {
      if ((form.earSide === 'left' || form.earSide === 'both') && !stlLeft) { toast.error("Veuillez uploader le fichier STL pour l'oreille gauche (OG)"); return; }
      if ((form.earSide === 'right' || form.earSide === 'both') && !stlRight) { toast.error("Veuillez uploader le fichier STL pour l'oreille droite (OD)"); return; }
    }
    if (wants3dValidation) {
      const em = (validationEmail || '').trim();
      if (!em || !/^\S+@\S+\.\S+$/.test(em)) {
        toast.error("Veuillez renseigner l'adresse email à laquelle envoyer le visuel 3D");
        return;
      }
    }

    setIsLoading(true);
    try {
      let order;

      if (editId) {
        // Edit mode: PUT /api/orders/{id}/client-edit
        const formData = form.buildFormData();
        formData.options.envoi_courrier = envoiCourrier;
        formData.options.wants_3d_validation = wants3dValidation;
        formData.options.validation_email = wants3dValidation ? (validationEmail || '').trim().toLowerCase() : null;
        if (selectedEstablishment) formData.establishment_id = selectedEstablishment;
        const res = await axios.put(`${API}/orders/${editId}/client-edit`, formData);
        order = res.data;
        setCreatedOrder({ ...order, _isEdit: true });
        toast.success('Commande modifiée avec succès !');
        return;
      }

      if (currentDraftId) {
        // Submit existing draft: first save the latest form data, then submit
        const formData = form.buildFormData();
        formData.options.envoi_courrier = envoiCourrier;
        formData.options.wants_3d_validation = wants3dValidation;
        formData.options.validation_email = wants3dValidation ? (validationEmail || '').trim().toLowerCase() : null;
        formData.is_refabrication = isRefab;
        formData.original_serial_number = isRefab ? refabSerial : null;
        if (selectedEstablishment) formData.establishment_id = selectedEstablishment;
        await axios.put(`${API}/orders/draft/${currentDraftId}`, formData);
        const submitRes = await axios.post(`${API}/orders/draft/${currentDraftId}/submit`);
        order = submitRes.data;
      } else {
        // Regular new order
        const formData = form.buildFormData();
        formData.options.envoi_courrier = envoiCourrier;
        formData.options.wants_3d_validation = wants3dValidation;
        formData.options.validation_email = wants3dValidation ? (validationEmail || '').trim().toLowerCase() : null;
        formData.is_refabrication = isRefab;
        formData.original_serial_number = isRefab ? refabSerial : null;
        if (selectedEstablishment) formData.establishment_id = selectedEstablishment;
        const response = await axios.post(`${API}/orders`, formData);
        order = response.data;
      }

      if (!isRefab && !envoiCourrier && !reuseImpressions) {
        const uploads = [];
        if (stlLeft && (form.earSide === 'left' || form.earSide === 'both')) uploads.push(uploadSTLFile(order.id, stlLeft, 'left'));
        if (stlRight && (form.earSide === 'right' || form.earSide === 'both')) uploads.push(uploadSTLFile(order.id, stlRight, 'right'));
        const results = await Promise.all(uploads);
        if (results.some(r => !r)) toast.warning("Commande créée mais certains fichiers n'ont pas pu être uploadés");
      } else if (!isRefab && reuseImpressions) {
        try {
          await axios.post(`${API}/orders/${order.id}/reuse-impressions`);
          toast.success('Empreintes réutilisées depuis une commande précédente');
        } catch (err) {
          toast.error(err.response?.data?.detail || "Impossible de réutiliser les empreintes — veuillez uploader manuellement");
        }
      }

      setCreatedOrder(order);
      toast.success('Commande créée avec succès !');
      // Open the printable order recap in a new tab (auto-prints via inline script)
      setTimeout(() => openOrderRecap(order.id), 500);
      if (envoiCourrier) setTimeout(() => generateBordereauPDF(order), 800);
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Cas particulier : consentement RGPD/CGV manquant → redirection vers /consent
      if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'consents_required') {
        toast.error(detail.message || "Vous devez accepter la politique et les CGV avant de commander.");
        setTimeout(() => { window.location.href = '/consent'; }, 1500);
        return;
      }
      toast.error(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Erreur lors de la création de la commande'));
    } finally { setIsLoading(false); }
  };

  if (createdOrder) {
    return (
      <div className="min-h-screen bg-zinc-100" data-testid="order-success">
        <header className="bg-white border-b border-zinc-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
            <Ear size={28} weight="duotone" className="text-zinc-900" />
            <span className="font-heading font-bold text-lg tracking-tight">HAMAC SOLUTIONS</span>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="bg-white border border-zinc-200 p-6 sm:p-8 text-center">
            <CheckCircle size={64} className="mx-auto mb-4 text-emerald-600" weight="duotone" />
            <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-2">Commande enregistrée !</h1>
            <p className="text-zinc-600 mb-6">Votre commande a été transmise à notre équipe.</p>

            {envoiCourrier && (
              <div className="bg-amber-50 border border-amber-200 p-4 mb-6 rounded-sm text-left max-w-md mx-auto">
                <div className="flex items-start gap-3">
                  <Envelope size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 mb-1">Envoi par courrier</p>
                    <p className="text-sm text-amber-700">Imprimez le bordereau et joignez-le à votre colis.</p>
                  </div>
                </div>
                <Button onClick={() => generateBordereauPDF(createdOrder)} variant="outline" size="sm" className="mt-3 rounded-sm w-full border-amber-300">
                  <Printer size={16} className="mr-2" />Réimprimer le bordereau
                </Button>
              </div>
            )}

            <div className="bg-zinc-50 border border-zinc-200 p-4 mb-6 text-left max-w-md mx-auto rounded-sm space-y-2">
              <p><span className="label-overline">Réf. Patient :</span> <span className="font-medium">{createdOrder.patient_reference}</span></p>
              <p><span className="label-overline">Produit :</span> <span className="font-medium">{createdOrder.product_name}</span></p>
              {createdOrder.options?.forme_embout && <p><span className="label-overline">Forme :</span> <span className="font-medium">{createdOrder.options.forme_embout}</span></p>}
              {createdOrder.material && <p><span className="label-overline">Matière :</span> <span className="font-medium">{createdOrder.material === 'resine_3d' ? 'Résine 3D' : 'Silicone'}</span></p>}
              {createdOrder.selected_options?.length > 0 && (
                <p><span className="label-overline">Options :</span> <span className="font-medium">{createdOrder.selected_options.map(o => o.name).join(', ')}</span></p>
              )}
              {createdOrder.serial_number_left && <p><span className="label-overline">N° série OG :</span> <span className="font-mono">{createdOrder.serial_number_left}</span></p>}
              {createdOrder.serial_number_right && <p><span className="label-overline">N° série OD :</span> <span className="font-mono">{createdOrder.serial_number_right}</span></p>}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button variant="outline" onClick={() => openOrderRecap(createdOrder.id)} className="rounded-sm" data-testid="reprint-recap-btn">
                <Printer size={16} className="mr-2" />Réimprimer le récapitulatif
              </Button>
              <Link to="/client"><Button variant="outline" className="rounded-sm">Retour au dashboard</Button></Link>
              <Link to="/client/order"><Button className="rounded-sm bg-zinc-900 hover:bg-zinc-800" onClick={() => window.location.reload()}>Nouvelle commande</Button></Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Ear size={28} weight="duotone" className="text-zinc-900" />
            <span className="font-heading font-bold text-lg tracking-tight">HAMAC SOLUTIONS</span>
          </div>
          <Link to="/client"><Button variant="outline" size="sm" className="rounded-sm"><ArrowLeft size={18} className="mr-1 sm:mr-2" /><span className="hidden sm:inline">Retour</span></Button></Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-2">
          {editId ? 'Modifier la commande' : (currentDraftId ? 'Reprendre le brouillon' : 'Formulaire de commande sur mesure')}
        </h1>
        <p className="text-zinc-600 text-sm sm:text-base mb-6">
          {editId
            ? 'Modifiez les informations ci-dessous puis enregistrez. La fabrication n\'a pas encore commencé.'
            : (currentDraftId
              ? 'Complétez votre brouillon, ajoutez vos fichiers STL, puis validez la commande.'
              : 'Complétez les informations ci-dessous, ajoutez vos fichiers STL, puis validez la commande.')}
        </p>

        {/* Refabrication */}
        <div className="bg-violet-50 border border-violet-200 p-4 mb-6 rounded-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ArrowClockwise size={24} className="text-violet-600 flex-shrink-0" />
              <div><p className="font-medium text-sm sm:text-base">Demande de refabrication</p><p className="text-xs sm:text-sm text-violet-600">Refaire un embout existant à l'identique</p></div>
            </div>
            <Checkbox checked={isRefab} onCheckedChange={setIsRefab} data-testid="refab-checkbox" />
          </div>
          {isRefab && (
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4">
              <Input placeholder="Numéro de série de l'embout à refaire" value={refabSerial} onChange={(e) => setRefabSerial(e.target.value)} className="flex-1 rounded-sm font-mono" data-testid="refab-serial-input" />
              <Button type="button" onClick={checkRefabrication} className="rounded-sm bg-violet-600 hover:bg-violet-700" data-testid="check-refab-btn"><MagnifyingGlass size={18} className="mr-2" />Vérifier</Button>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-0">
          {user?.role === 'client' && !(user?.establishment_ids?.length) && (
            <div className="bg-amber-50 border border-amber-200 p-4 mb-5 rounded-sm text-sm text-amber-800" data-testid="orderform-no-establishment">
              Votre compte n'est associé à aucun établissement : la création de commande est impossible. Contactez HAMAC pour activer votre accès.
            </div>
          )}
          {/* Establishment selection for multi-establishment clients */}
          {establishments.length > 1 && (
            <div className={`bg-white border p-4 sm:p-6 mb-5 sm:mb-6 rounded-sm ${selectedEstablishment ? 'border-zinc-200' : 'border-amber-400'}`} data-testid="establishment-selection">
              <Label className="label-overline mb-2 block">Établissement *</Label>
              <Select value={selectedEstablishment} onValueChange={setSelectedEstablishment}>
                <SelectTrigger className="rounded-sm bg-white" data-testid="establishment-select"><SelectValue placeholder="Sélectionner un établissement" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {establishments.map(e => <SelectItem key={e.id} value={e.id} data-testid={`establishment-option-${e.id}`}>{e.name}{e.ville ? ` — ${e.ville}` : (e.address ? ` — ${e.address}` : '')}</SelectItem>)}
                </SelectContent>
              </Select>
              {!selectedEstablishment && <p className="text-xs text-amber-700 mt-2" data-testid="establishment-required-hint">Sélection obligatoire : la commande et l'étiquette d'expédition seront rattachées à cet établissement.</p>}
            </div>
          )}
          {establishments.length === 1 && user?.role === 'client' && (
            <div className="bg-white border border-zinc-200 p-4 sm:p-5 mb-5 sm:mb-6 rounded-sm" data-testid="establishment-auto">
              <Label className="label-overline mb-1 block">Établissement</Label>
              <p className="font-medium text-sm" data-testid="establishment-auto-name">{establishments[0].name}</p>
              <p className="text-xs text-zinc-500">{[establishments[0].address, [establishments[0].code_postal, establishments[0].ville].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</p>
            </div>
          )}

          <OrderFormFields form={form} user={user} />

          {/* STL Upload */}
          {!isRefab && (
            <div className="bg-white border border-zinc-200 p-4 sm:p-6 mt-5 sm:mt-6">
              <h2 className="font-heading font-semibold text-base sm:text-lg mb-4 pb-2 border-b border-zinc-200">Empreintes / Fichiers STL</h2>

              {/* Réutilisation des empreintes d'une commande précédente */}
              {reuseAvailable?.available && !envoiCourrier && (
                <div className={`p-4 mb-4 rounded-sm border-2 ${reuseImpressions ? 'border-emerald-500 bg-emerald-50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="reuse-impressions-block">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <ArrowClockwise size={22} className={`flex-shrink-0 mt-0.5 ${reuseImpressions ? 'text-emerald-600' : 'text-zinc-500'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm sm:text-base">Utiliser les empreintes précédentes</p>
                        <p className="text-xs sm:text-sm text-zinc-500 mt-0.5">
                          Empreinte(s) trouvée(s) pour ce patient ({reuseAvailable.sides.map(s => s.side === 'left' ? 'OG' : 'OD').join(' + ')}). Réutilisez-les pour cette fabrication sans renvoyer de STL.
                        </p>
                        {reuseImpressions && (
                          <ul className="text-xs text-emerald-700 mt-2 space-y-0.5">
                            {reuseAvailable.sides.map(s => (
                              <li key={s.side} className="font-mono truncate" data-testid={`reuse-side-${s.side}`}>
                                ✓ {s.side === 'left' ? 'OG' : 'OD'} — {s.filename || 'empreinte précédente'}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <Checkbox checked={reuseImpressions} onCheckedChange={(c) => {
                      setReuseImpressions(c);
                      if (c) { setStlLeft(null); setStlRight(null); setEnvoiCourrier(false); }
                    }} data-testid="reuse-impressions-checkbox" />
                  </div>
                </div>
              )}

              <div className={`p-4 mb-4 rounded-sm border-2 ${envoiCourrier ? 'border-amber-400 bg-amber-50' : 'border-zinc-200 bg-zinc-50'} ${reuseImpressions ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Envelope size={24} className={`flex-shrink-0 ${envoiCourrier ? 'text-amber-600' : 'text-zinc-500'}`} />
                    <div><p className="font-medium text-sm sm:text-base">Envoi des empreintes par courrier</p><p className="text-xs sm:text-sm text-zinc-500">Je joindrai les moulages physiques à mon colis</p></div>
                  </div>
                  <Checkbox checked={envoiCourrier} onCheckedChange={(checked) => { setEnvoiCourrier(checked); if (checked) { setStlLeft(null); setStlRight(null); } }} data-testid="envoi-courrier-checkbox" />
                </div>
                {envoiCourrier && (
                  <div className="mt-3 pt-3 border-t border-amber-200 text-sm text-amber-700">
                    <p className="font-medium mb-1">Adresse d'envoi :</p>
                    <p>HAMAC SOLUTIONS<br/>67 rue de la République<br/>69220 Belleville</p>
                  </div>
                )}
              </div>
              {!envoiCourrier && !reuseImpressions && (
                <>
                  <p className="text-sm text-zinc-500 mb-4">Format accepté : .stl — Taille maximale : 15 Mo par fichier</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {(form.earSide === 'left' || form.earSide === 'both') && (
                      <div>
                        <p className="text-sm font-medium mb-2">Fichier STL OG (Oreille Gauche) *</p>
                        <div className={`dropzone ${stlLeft ? 'border-emerald-500 bg-emerald-50' : ''}`} onDrop={(e) => handleDrop(e, 'left')} onDragOver={handleDragOver} onClick={() => fileInputLeft.current?.click()} data-testid="dropzone-left">
                          <input type="file" ref={fileInputLeft} accept=".stl" className="hidden" onChange={(e) => handleFileSelect('left', e.target.files[0])} />
                          {stlLeft ? (<div className="flex items-center justify-center gap-3"><File size={24} className="text-emerald-600" /><span className="font-mono text-sm truncate max-w-[150px]">{stlLeft.name}</span><button type="button" onClick={(e) => { e.stopPropagation(); setStlLeft(null); }} className="text-zinc-500 hover:text-zinc-700"><X size={18} /></button></div>) : (<><Upload size={32} className="mx-auto mb-3 text-zinc-400" /><p className="text-sm text-zinc-600">Glissez le fichier STL ou cliquez</p></>)}
                        </div>
                      </div>
                    )}
                    {(form.earSide === 'right' || form.earSide === 'both') && (
                      <div>
                        <p className="text-sm font-medium mb-2">Fichier STL OD (Oreille Droite) *</p>
                        <div className={`dropzone ${stlRight ? 'border-emerald-500 bg-emerald-50' : ''}`} onDrop={(e) => handleDrop(e, 'right')} onDragOver={handleDragOver} onClick={() => fileInputRight.current?.click()} data-testid="dropzone-right">
                          <input type="file" ref={fileInputRight} accept=".stl" className="hidden" onChange={(e) => handleFileSelect('right', e.target.files[0])} />
                          {stlRight ? (<div className="flex items-center justify-center gap-3"><File size={24} className="text-emerald-600" /><span className="font-mono text-sm truncate max-w-[150px]">{stlRight.name}</span><button type="button" onClick={(e) => { e.stopPropagation(); setStlRight(null); }} className="text-zinc-500 hover:text-zinc-700"><X size={18} /></button></div>) : (<><Upload size={32} className="mx-auto mb-3 text-zinc-400" /><p className="text-sm text-zinc-600">Glissez le fichier STL ou cliquez</p></>)}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Visualisation 3D */}
          <div className="bg-zinc-50 border border-zinc-200 p-4 sm:p-5 rounded-sm" data-testid="viz3d-section">
            <div className="flex items-start gap-3">
              <Checkbox
                id="wants_3d_validation"
                checked={wants3dValidation}
                onCheckedChange={(checked) => {
                  setWants3dValidation(!!checked);
                  if (checked && !validationEmail) {
                    setValidationEmail(lastValidationEmail || user?.email || '');
                  }
                }}
                className="mt-0.5"
                data-testid="viz3d-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="wants_3d_validation" className="font-medium text-sm cursor-pointer">
                  Visualisation 3D avant fabrication
                </Label>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                  En activant cette option, vous pourrez visualiser et valider votre embout avant fabrication.
                  Les délais de production et d'expédition pourront être allongés en fonction du temps nécessaire
                  à votre validation ou à d'éventuelles demandes de modification.
                </p>
                {wants3dValidation && (
                  <div className="mt-3 bg-white border border-zinc-300 rounded-sm p-3" data-testid="viz3d-email-block">
                    <Label htmlFor="validation_email" className="text-xs font-semibold flex items-center gap-1.5">
                      <Envelope size={14} />
                      Adresse email pour recevoir le visuel 3D *
                    </Label>
                    <Input
                      id="validation_email"
                      type="email"
                      value={validationEmail}
                      onChange={(e) => setValidationEmail(e.target.value)}
                      placeholder="email@audioprothesiste.fr"
                      className="rounded-sm mt-1.5 text-sm"
                      required
                      data-testid="viz3d-email-input"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1.5">
                      Le lien de visualisation 3D sera envoyé à cette adresse. Elle sera conservée pour vos prochaines commandes.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col gap-3 mt-5 sm:mt-6">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto sm:ml-auto">
              <Link to="/client" className="w-full sm:w-auto"><Button type="button" variant="outline" className="rounded-sm w-full">Annuler</Button></Link>
              <Button type="button" variant="outline" className="rounded-sm w-full sm:w-auto" onClick={handleSaveDraft} disabled={isSavingDraft} data-testid="save-draft-btn">
                <FloppyDisk size={16} className="mr-1" />
                {isSavingDraft ? 'Sauvegarde...' : 'Sauvegarder brouillon'}
              </Button>
              <Button type="submit" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 w-full sm:w-auto sm:px-8" disabled={isLoading} data-testid="submit-order-btn">
                {isLoading ? <span className="spinner" /> : (editId ? 'Enregistrer les modifications' : 'Valider la commande')}
              </Button>
            </div>
            <p className="text-sm text-zinc-500 text-center sm:text-left">* Champs obligatoires</p>
          </div>
        </form>
      </main>

      {/* Refabrication dialog */}
      <Dialog open={refabDialogOpen} onOpenChange={setRefabDialogOpen}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirmation de refabrication</DialogTitle>
            <DialogDescription>
              {refabInfo && (
                <div className="mt-4 space-y-3">
                  <p><strong>Référence patient:</strong> {refabInfo.patient_reference}</p>
                  <p><strong>Date commande originale:</strong> {new Date(refabInfo.original_date).toLocaleDateString('fr-FR')}</p>
                  <div className={`p-4 rounded-sm ${refabInfo.is_free ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                    <p className={refabInfo.is_free ? 'text-emerald-700 font-medium' : 'text-amber-700 font-medium'}>{refabInfo.message}</p>
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setRefabDialogOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={confirmRefabrication} className="rounded-sm bg-violet-600 hover:bg-violet-700" data-testid="confirm-refab-btn">Confirmer la refabrication</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
